import { Fragment, useMemo } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import { useSelect, useDispatch } from '@wordpress/data';
import { Stack, Text } from '@wordpress/ui';
import {
	store as blockEditorStore,
	privateApis as blockEditorPrivateApis,
} from '@wordpress/block-editor';
import { unlock } from '../../lock-unlock';
import { NoteThread } from './note-thread';
import { focusNoteThread } from './utils';
import { useFloatingBoard, useNoteActions, useNoteSelection } from './hooks';
import { AddNote } from './add-note';
import { store as editorStore } from '../../store';

const { useBlockElement } = unlock( blockEditorPrivateApis );

export function Notes( { notes, sidebarRef, isFloating = false } ) {
	const {
		onCreate: onAddReply,
		onEdit: onEditNote,
		onDelete,
	} = useNoteActions();
	const { selectNote } = unlock( useDispatch( editorStore ) );
	const { selectBlock, toggleBlockSpotlight } = unlock(
		useDispatch( blockEditorStore )
	);

	const { selectedBlockClientId, orderedBlockIds } = useSelect(
		( select ) => {
			const { getSelectedBlockClientId, getClientIdsWithDescendants } =
				select( blockEditorStore );
			return {
				selectedBlockClientId: getSelectedBlockClientId(),
				orderedBlockIds: getClientIdsWithDescendants(),
			};
		},
		[]
	);
	const selectedNote = useSelect(
		( select ) => unlock( select( editorStore ) ).getSelectedNote(),
		[]
	);

	useNoteSelection( { notes, sidebarRef } );

	const relatedBlockElement = useBlockElement( selectedBlockClientId );

	const threads = useMemo( () => {
		// In floating mode with a pending new note, splice a placeholder
		// entry at the selected block's position so the board can float it
		// alongside regular threads.
		if ( ! isFloating || selectedNote !== 'new' ) {
			return notes;
		}
		const newNoteThread = {
			id: 'new',
			blockClientId: selectedBlockClientId,
			content: { rendered: '' },
		};
		const out = [];
		orderedBlockIds.forEach( ( blockId ) => {
			// Blocks can carry multiple notes — surface them all.
			const threadsForBlock = notes.filter(
				( t ) => t.blockClientId === blockId
			);
			out.push( ...threadsForBlock );
			if ( blockId === selectedBlockClientId ) {
				// Place the new note placeholder after the block's existing
				// threads so the form appears alongside them.
				out.push( newNoteThread );
			}
		} );
		return out;
	}, [
		notes,
		isFloating,
		selectedNote,
		selectedBlockClientId,
		orderedBlockIds,
	] );

	const handleDelete = async ( note ) => {
		const currentIndex = threads.findIndex( ( t ) => t.id === note.id );
		const nextThread = threads[ currentIndex + 1 ];
		const prevThread = threads[ currentIndex - 1 ];

		const deleted = await onDelete( note );
		// Leave the selection alone when the delete failed; the note is still there.
		if ( ! deleted ) {
			return;
		}

		if ( note.parent !== 0 ) {
			// Move focus to the parent thread when a reply was deleted.
			selectNote( note.parent );
			focusNoteThread( note.parent, sidebarRef.current );
			return;
		}

		const adjacentThread = nextThread ?? prevThread;
		if ( adjacentThread ) {
			selectNote( adjacentThread.id );
			focusNoteThread( adjacentThread.id, sidebarRef.current );
			if ( adjacentThread.blockClientId ) {
				toggleBlockSpotlight( adjacentThread.blockClientId, true );
				// Pass `null` as the second parameter to prevent focusing the block.
				selectBlock( adjacentThread.blockClientId, null );
			}
		} else {
			selectNote( undefined );
			toggleBlockSpotlight( note.blockClientId, false );
			// Move focus to the related block.
			relatedBlockElement?.focus();
		}
	};

	const { notePositions, heights, registerThread, unregisterThread } =
		useFloatingBoard( {
			threads,
			selectedNoteId: selectedNote,
			isFloating,
			sidebarRef,
		} );

	const navigate = ( event, thread, isSelected ) => {
		if ( event.defaultPrevented ) {
			return;
		}

		const currentIndex = threads.findIndex( ( t ) => t.id === thread.id );
		const isSelfTarget = event.currentTarget === event.target;

		if (
			( event.key === 'Enter' || event.key === 'ArrowRight' ) &&
			isSelfTarget &&
			! isSelected
		) {
			// Expand thread.
			selectNote( thread.id );
			if ( !! thread.blockClientId ) {
				// Pass `null` as the second parameter to prevent focusing the block.
				selectBlock( thread.blockClientId, null );
				toggleBlockSpotlight( thread.blockClientId, true );
			}
		} else if (
			( ( event.key === 'Enter' || event.key === 'ArrowLeft' ) &&
				isSelfTarget &&
				isSelected ) ||
			event.key === 'Escape'
		) {
			// Collapse thread.
			selectNote( undefined );
			if ( thread.blockClientId ) {
				toggleBlockSpotlight( thread.blockClientId, false );
			}
			focusNoteThread( thread.id, sidebarRef.current );
		} else if ( isSelfTarget ) {
			const targetIndex = {
				ArrowDown: Math.min( currentIndex + 1, threads.length - 1 ),
				ArrowUp: Math.max( currentIndex - 1, 0 ),
				Home: 0,
				End: threads.length - 1,
			}[ event.key ];
			if ( targetIndex !== undefined ) {
				// The floating panel scrolls; keep the key from scrolling it too.
				event.preventDefault();
				focusNoteThread(
					threads[ targetIndex ].id,
					sidebarRef.current
				);
			}
		}
	};

	// In the "All notes" view, find where the resolved notes begin so a
	// "Resolved" divider can be rendered above them. Resolved notes (status
	// 'approved' with a still-present block) always sort after the active
	// ones, so the first match marks the boundary. The floating view only
	// lists unresolved notes, so it needs no divider.
	const firstResolvedIndex = isFloating
		? -1
		: threads.findIndex(
				( thread ) =>
					thread.status === 'approved' && !! thread.blockClientId
			);
	const isAddingNote =
		! isFloating && selectedNote === 'new' && !! selectedBlockClientId;

	return (
		<Stack
			className="editor-collab-sidebar-panel"
			role="tree"
			direction="column"
			gap="md"
			justify="flex-start"
			ref={ ( node ) => {
				// Sometimes previous sidebar unmounts after the new one mounts.
				// This ensures we always have the latest reference.
				if ( node ) {
					sidebarRef.current = node;
				}
			} }
			aria-label={
				isFloating ? __( 'Unresolved notes' ) : __( 'All notes' )
			}
		>
			{ isAddingNote && (
				<AddNote
					key={ selectedBlockClientId }
					clientId={ selectedBlockClientId }
					onSubmit={ onAddReply }
					sidebarRef={ sidebarRef }
				/>
			) }
			{ threads.map( ( thread, index ) => (
				<Fragment key={ thread.id }>
					{ index === firstResolvedIndex && (
						<Stack
							direction="row"
							align="center"
							justify="center"
							gap="sm"
							className="editor-collab-sidebar-panel__status-separator"
						>
							<Text variant="heading-sm" render={ <p /> }>
								{ __( 'Resolved' ) }
							</Text>
						</Stack>
					) }
					<NoteThread
						note={ thread }
						onAddReply={ onAddReply }
						onDeleteNote={ handleDelete }
						onEditNote={ onEditNote }
						isSelected={ selectedNote === thread.id }
						sidebarRef={ sidebarRef }
						floating={
							isFloating
								? {
										y: notePositions[ thread.id ],
										height: heights[ thread.id ],
										registerThread,
										unregisterThread,
									}
								: undefined
						}
						onKeyDown={ ( event ) =>
							navigate(
								event,
								thread,
								selectedNote === thread.id
							)
						}
					/>
				</Fragment>
			) ) }
		</Stack>
	);
}
